import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ChatInterface.module.css';
import {
    getConversation,
    sendMessage,
    markMessagesAsRead,
    getUnreadCount,
    clearError,
    addOptimisticMessage,
    getHelpdeskTickets,
    getHelpdeskTicketDetail,
    getHelpdeskCategories,
    createHelpdeskTicket,
    sendHelpdeskMessage,
    escalateHelpdeskTicket,
    reopenHelpdeskTicket,
    setSelectedTicketId,
    clearTicketSuccessMessage,
} from '../features/internalChat/internalChatSlice';

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'xlsx', 'docx'];
const MAX_FILES_PER_UPLOAD = 1;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const URGENCY_LEVELS = ['low', 'medium', 'high', 'critical'];

function formatTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(date) {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString();
}

function titleCaseStatus(status = '') {
    return String(status)
        .split('_')
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(' ');
}

function getTicketSourceFromLocation() {
    const pathname = window.location.pathname || '';
    if (pathname.includes('tara')) return 'tara';
    if (pathname.includes('terminal')) return 'terminal';
    return 'console';
}

function validateAttachments(files) {
    if (!files || files.length === 0) return null;

    if (files.length > MAX_FILES_PER_UPLOAD) {
        return `You can upload up to ${MAX_FILES_PER_UPLOAD} files at once.`;
    }

    for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return `${file.name} is larger than 10 MB.`;
        }

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
            return `${file.name} has an unsupported format. Allowed: PNG, JPG, PDF, XLSX, DOCX.`;
        }
    }

    return null;
}

export default function InternalChat({ brandId = null }) {
    const dispatch = useDispatch();
    const {
        messages,
        loading,
        error,
        sendingMessage,
        conversation,
        unreadCount,
        tickets,
        categories,
        categoriesLoading,
        selectedTicketId,
        ticketDetail,
        ticketsLoading,
        ticketDetailLoading,
        ticketActionLoading,
        creatingTicket,
        ticketSuccessMessage,
        ticketsPagination,
    } = useSelector((state) => state.internalChat);

    const { user_type } = useSelector((state) => state.user);
    const isHelpdeskMode = user_type !== 'employee';

    const [legacyInput, setLegacyInput] = useState('');
    const [hasInitializedLegacy, setHasInitializedLegacy] = useState(false);

    const [ticketFilter, setTicketFilter] = useState('');
    const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
    const [ticketForm, setTicketForm] = useState({
        title: '',
        description: '',
        category: '',
        urgency: 'medium',
        source: getTicketSourceFromLocation(),
        book_call: false,
    });
    const [ticketAttachments, setTicketAttachments] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [messageAttachments, setMessageAttachments] = useState([]);
    const [showReopenModal, setShowReopenModal] = useState(false);

    const [reopenContext, setReopenContext] = useState('');

    const chatEndRef = useRef(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, ticketDetail]);

    useEffect(() => {
        if (!isHelpdeskMode) return;
        dispatch(getHelpdeskCategories());
        dispatch(getHelpdeskTickets({ status: ticketFilter || undefined, page: 1, per_page: 20 }));
    }, [dispatch, isHelpdeskMode, ticketFilter]);

    // Set initial category once categories are loaded
    useEffect(() => {
        if (categories.length > 0 && !ticketForm.category) {
            setTicketForm((prev) => ({ ...prev, category: categories[0].id }));
        }
    }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isHelpdeskMode || !selectedTicketId) return;
        dispatch(getHelpdeskTicketDetail(selectedTicketId));
    }, [dispatch, isHelpdeskMode, selectedTicketId]);

    // Poll for new messages (e.g. admin replies) every 5 seconds while a ticket is open
    useEffect(() => {
        if (!isHelpdeskMode || !selectedTicketId) return undefined;
        const interval = setInterval(() => {
            dispatch(getHelpdeskTicketDetail(selectedTicketId));
        }, 5000);
        return () => clearInterval(interval);
    }, [dispatch, isHelpdeskMode, selectedTicketId]);

    useEffect(() => {
        if (!isHelpdeskMode || !ticketSuccessMessage) return;
        const timer = setTimeout(() => dispatch(clearTicketSuccessMessage()), 5000);
        return () => clearTimeout(timer);
    }, [dispatch, isHelpdeskMode, ticketSuccessMessage]);

    useEffect(() => {
        if (isHelpdeskMode || hasInitializedLegacy) return;

        setHasInitializedLegacy(true);
        const params = brandId ? { brand_id: brandId } : {};
        dispatch(getConversation(params));
        dispatch(getUnreadCount(params));
    }, [dispatch, brandId, hasInitializedLegacy, isHelpdeskMode]);

    useEffect(() => {
        if (isHelpdeskMode) return undefined;

        if (messages.length > 0 && unreadCount > 0) {
            const timer = setTimeout(() => {
                const params = brandId ? { brand_id: brandId } : {};
                dispatch(markMessagesAsRead(params));
            }, 1000);
            return () => clearTimeout(timer);
        }

        return undefined;
    }, [messages.length, unreadCount, dispatch, brandId, isHelpdeskMode]);

    useEffect(() => {
        if (!error) return undefined;
        const timer = setTimeout(() => dispatch(clearError()), 5000);
        return () => clearTimeout(timer);
    }, [error, dispatch]);

    const handleLegacySend = async (event) => {
        event.preventDefault();
        if (!legacyInput.trim() || sendingMessage) return;

        const messageContent = legacyInput.trim();
        setLegacyInput('');

        const senderType = user_type === 'employee' ? 'employee' : 'brand_user';
        const senderName = user_type === 'employee' ? 'Support Team' : 'Brand User';

        dispatch(
            addOptimisticMessage({
                content: messageContent,
                sender_type: senderType,
                sender_name: senderName,
            })
        );

        const messageData = { content: messageContent };
        if (user_type === 'appuser') {
            messageData.employee_id = 1;
        } else if (user_type === 'employee' && brandId) {
            messageData.brand_id = brandId;
        }

        try {
            await dispatch(sendMessage(messageData)).unwrap();
            const params = brandId ? { brand_id: brandId } : {};
            dispatch(getUnreadCount(params));
        } catch (sendError) {
            console.error('Failed to send message:', sendError);
        }
    };

    const getSenderDisplayName = (message) => {
        if (message.sender_type === 'employee') {
            return message.sender_name || 'Support Team';
        }
        return message.sender_name || 'Brand User';
    };

    const isCurrentUserMessage = (message) => {
        if (user_type === 'employee') return message.sender_type === 'employee';
        if (user_type === 'appuser') return message.sender_type === 'brand_user' || message.sender_type === 'appuser';
        return false;
    };

    const refreshTicketsAndDetail = async (ticketId = selectedTicketId) => {
        await dispatch(getHelpdeskTickets({ status: ticketFilter || undefined, page: 1, per_page: 20 }));
        if (ticketId) await dispatch(getHelpdeskTicketDetail(ticketId));
    };

    const handleCreateTicket = async (event) => {
        event.preventDefault();

        const filesError = validateAttachments(ticketAttachments);
        if (filesError) {
            alert(filesError);
            return;
        }

        try {
            const result = await dispatch(
                createHelpdeskTicket({
                    ...ticketForm,
                    attachments: ticketAttachments,
                })
            ).unwrap();

            setShowCreateTicketForm(false);
            setTicketForm({
                title: '',
                description: '',
                category: categories[0]?.id || '',
                urgency: 'medium',
                source: getTicketSourceFromLocation(),
                book_call: false,
            });
            setTicketAttachments([]);

            if (ticketForm.book_call && result?.calendly_url) {
                window.open(result.calendly_url, '_blank', 'noopener,noreferrer');
            }

            await refreshTicketsAndDetail(result.ticket_id);
        } catch (createError) {
            console.error('Failed to create ticket', createError);
        }
    };

    const handleSelectTicket = (ticketId) => {
        dispatch(setSelectedTicketId(ticketId));
        setShowCreateTicketForm(false);
    };

    const handleSendHelpdeskMessage = async (event) => {
        event.preventDefault();
        if (!selectedTicketId) return;
        if (!messageInput.trim()) return;

        const filesError = validateAttachments(messageAttachments);
        if (filesError) {
            alert(filesError);
            return;
        }

        try {
            await dispatch(
                sendHelpdeskMessage({
                    ticketId: selectedTicketId,
                    content: messageInput.trim(),
                    attachments: messageAttachments,
                })
            ).unwrap();

            setMessageInput('');
            setMessageAttachments([]);
            await refreshTicketsAndDetail(selectedTicketId);
        } catch (sendError) {
            console.error('Failed to send helpdesk message', sendError);
        }
    };

    const handleEscalateTicket = async () => {
        if (!selectedTicketId) return;
        try {
            await dispatch(escalateHelpdeskTicket(selectedTicketId)).unwrap();
            await refreshTicketsAndDetail(selectedTicketId);
        } catch (escalateError) {
            console.error('Failed to escalate ticket', escalateError);
        }
    };

    const handleReopenTicket = async (event) => {
        event.preventDefault();
        if (!selectedTicketId || !reopenContext.trim()) return;

        try {
            await dispatch(
                reopenHelpdeskTicket({
                    ticketId: selectedTicketId,
                    context: reopenContext.trim(),
                })
            ).unwrap();

            setShowReopenModal(false);
            setReopenContext('');
            await refreshTicketsAndDetail(selectedTicketId);
        } catch (reopenError) {
            console.error('Failed to reopen ticket', reopenError);
        }
    };

    const openCalendlyLink = (url) => {
        if (!url) {
            alert('Call booking is not yet configured for this ticket category. Please contact support for assistance.');
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const renderAttachments = (attachments = []) => {
        if (!attachments.length) return null;

        return (
            <div className={styles.messageAttachments}>
                {attachments.map((attachment, index) => {
                    const href = attachment.file_url || '#';
                    const key = attachment.attachment_id || attachment.id || `${attachment.file_name}-${index}`;
                    return (
                        <a
                            key={key}
                            className={styles.attachmentPill}
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {attachment.file_name || 'Attachment'}
                        </a>
                    );
                })}
            </div>
        );
    };

    const renderHelpdeskMode = () => {
        const ticketObject = ticketDetail?.ticket || ticketDetail || {};
        const detailMessages = ticketDetail?.messages || [];
        const selectedStatus = ticketObject.status || '';
        const canReply = selectedStatus !== 'resolved' && selectedStatus !== 'closed';
        const canEscalate = Boolean(ticketDetail?.can_escalate);
        const calendlyUrl = ticketObject.category_detail?.calendly_url || ticketDetail?.calendly_url || null;

        return (
            <div className={styles.helpdeskRoot}>
                {error && <div className={styles.errorDisplay}>{error}</div>}
                {ticketSuccessMessage && <div className={styles.successDisplay}>{ticketSuccessMessage}</div>}

                <div className={styles.helpdeskTopBar}>
                    <div>
                        <div className={styles.chatTitle}>Suspire Helpdesk</div>
                        <div className={styles.helpdeskSubTitle}>Raise tickets, track updates, and chat with support.</div>
                    </div>
                    <button
                        className={styles.primaryActionButton}
                        type="button"
                        onClick={() => setShowCreateTicketForm((value) => !value)}
                    >
                        {showCreateTicketForm ? 'Close Form' : 'Create Ticket'}
                    </button>
                </div>

                <div className={styles.helpdeskLayout}>
                    <aside className={styles.ticketListPane}>
                        <div className={styles.ticketListControls}>
                            <select
                                className={styles.filterSelect}
                                value={ticketFilter}
                                onChange={(e) => setTicketFilter(e.target.value)}
                            >
                                <option value="">All statuses</option>
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                            <div className={styles.ticketCountLabel}>
                                {ticketsPagination?.total || tickets.length} tickets
                            </div>
                        </div>

                        {ticketsLoading ? (
                            <div className={styles.smallLoading}>Loading tickets...</div>
                        ) : tickets.length === 0 ? (
                            <div className={styles.emptyState}>No tickets found for this filter.</div>
                        ) : (
                            <div className={styles.ticketList}>
                                {tickets.map((ticket) => {
                                    const ticketId = ticket.ticket_id || ticket.id;
                                    const isActive = ticketId === selectedTicketId;
                                    return (
                                        <button
                                            key={ticketId}
                                            type="button"
                                            className={`${styles.ticketCard} ${isActive ? styles.ticketCardActive : ''}`}
                                            onClick={() => handleSelectTicket(ticketId)}
                                        >
                                            <div className={styles.ticketCardHeader}>
                                                <span className={styles.ticketDisplayId}>{ticket.display_id || ticketId}</span>
                                                <span className={`${styles.statusBadge} ${styles[`status_${ticket.status}`] || ''}`}>
                                                    {titleCaseStatus(ticket.status)}
                                                </span>
                                            </div>
                                            <div className={styles.ticketTitle}>{ticket.title}</div>
                                            <div className={styles.ticketMetaRow}>
                                                <span>{titleCaseStatus(ticket.category)}</span>
                                                <span>{titleCaseStatus(ticket.urgency)}</span>
                                            </div>
                                            <div className={styles.ticketMetaRow}>
                                                <span>{formatDateTime(ticket.last_activity_at)}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </aside>

                    <section className={styles.ticketDetailPane}>
                        {showCreateTicketForm ? (
                            <form className={styles.createTicketForm} onSubmit={handleCreateTicket}>
                                <h3>Create Support Ticket</h3>
                                <label className={styles.formLabel}>
                                    Title
                                    <input
                                        className={styles.formInput}
                                        type="text"
                                        value={ticketForm.title}
                                        onChange={(e) => setTicketForm((prev) => ({ ...prev, title: e.target.value }))}
                                        maxLength={255}
                                        required
                                    />
                                </label>
                                <label className={styles.formLabel}>
                                    Description
                                    <textarea
                                        className={styles.formTextarea}
                                        value={ticketForm.description}
                                        onChange={(e) => setTicketForm((prev) => ({ ...prev, description: e.target.value }))}
                                        rows={5}
                                        required
                                    />
                                </label>

                                <div className={styles.formGrid}>
                                    <label className={styles.formLabel}>
                                        Category
                                        <select
                                            className={styles.formInput}
                                            value={ticketForm.category}
                                            onChange={(e) => setTicketForm((prev) => ({ ...prev, category: e.target.value }))}
                                        >
                                            {categoriesLoading ? (
                                                <option value="">Loading categories...</option>
                                            ) : categories.length > 0 ? (
                                                categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="">No categories available</option>
                                            )}
                                        </select>
                                    </label>

                                    <label className={styles.formLabel}>
                                        Urgency
                                        <select
                                            className={styles.formInput}
                                            value={ticketForm.urgency}
                                            onChange={(e) => setTicketForm((prev) => ({ ...prev, urgency: e.target.value }))}
                                        >
                                            {URGENCY_LEVELS.map((urgency) => (
                                                <option key={urgency} value={urgency}>
                                                    {titleCaseStatus(urgency)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <label className={styles.formLabel}>
                                    Attachment (PNG, JPG, PDF, XLSX, DOCX | max 1 file, 10 MB)
                                    <input
                                        className={styles.formInput}
                                        type="file"
                                        accept=".png,.jpg,.jpeg,.pdf,.xlsx,.docx"
                                        onChange={(e) => setTicketAttachments(Array.from(e.target.files || []))}
                                    />
                                </label>

                                {ticketAttachments.length > 0 && (
                                    <div className={styles.attachmentPreviewList}>
                                        {ticketAttachments.map((file) => (
                                            <span key={file.name + file.size} className={styles.attachmentPill}>
                                                {file.name}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <button type="submit" className={styles.primaryActionButton} disabled={creatingTicket}>
                                    {creatingTicket ? 'Creating...' : 'Submit Ticket'}
                                </button>
                            </form>
                        ) : !selectedTicketId ? (
                            <div className={styles.emptyState}>Create your first ticket to start the conversation.</div>
                        ) : ticketDetailLoading ? (
                            <div className={styles.smallLoading}>Loading ticket detail...</div>
                        ) : (
                            <>
                                <div className={styles.ticketDetailHeader}>
                                    <div>
                                        <div className={styles.ticketDetailTitle}>{ticketObject.title || 'Support Ticket'}</div>
                                        <div className={styles.ticketMetaRow}>
                                            <span>{ticketObject.display_id || selectedTicketId}</span>
                                            <span>{titleCaseStatus(ticketObject.category)}</span>
                                            <span>{titleCaseStatus(ticketObject.urgency)}</span>
                                            <span className={`${styles.statusBadge} ${styles[`status_${selectedStatus}`] || ''}`}>
                                                {titleCaseStatus(selectedStatus)}
                                            </span>
                                        </div>
                                        <div className={styles.slaText}>
                                            {ticketDetail?.sla_text || 'Typical response time: within 24 hours.'}
                                        </div>
                                    </div>

                                    <div className={styles.ticketActionsRow}>
                                        <button
                                            type="button"
                                            className={styles.secondaryActionButton}
                                            disabled={!canEscalate || ticketActionLoading}
                                            title={
                                                canEscalate
                                                    ? 'Escalate this ticket'
                                                    : 'Escalation is available after 48 hours of inactivity.'
                                            }
                                            onClick={handleEscalateTicket}
                                        >
                                            Escalate
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.secondaryActionButton}
                                            onClick={() => openCalendlyLink(calendlyUrl)}
                                            title="Book a category-mapped support call"
                                        >
                                            Book a Call
                                        </button>
                                        {selectedStatus === 'resolved' && (
                                            <button
                                                type="button"
                                                className={styles.primaryActionButton}
                                                onClick={() => setShowReopenModal(true)}
                                            >
                                                Reopen
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.messagesArea}>
                                    {detailMessages.length === 0 ? (
                                        <div className={styles.emptyState}>No messages in this ticket yet.</div>
                                    ) : (
                                        detailMessages.map((msg, index) => {
                                            const senderType = msg.sender_type || msg.senderType;
                                            const isUserMessage = senderType === 'brand' || senderType === 'brand_user';
                                            return (
                                                <div
                                                    key={msg.message_id || msg.id || index}
                                                    className={`${styles.messageContainer} ${isUserMessage ? styles.sender : styles.receiver}`}
                                                >
                                                    <div
                                                        className={`${styles.messageBubble} ${isUserMessage ? styles.sender : styles.receiver}`}
                                                    >
                                                        <div
                                                            className={`${styles.senderName} ${isUserMessage ? styles.sender : styles.receiver}`}
                                                        >
                                                            {isUserMessage ? 'You' : 'Support Team'}
                                                        </div>
                                                        <div className={styles.messageContent}>{msg.content}</div>
                                                        {renderAttachments(msg.attachments || [])}
                                                        <div
                                                            className={`${styles.messageFooter} ${isUserMessage ? styles.sender : styles.receiver}`}
                                                        >
                                                            <span>{formatTime(msg.created_at || msg.sent_at || msg.time)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={chatEndRef} />
                                </div>

                                {canReply ? (
                                    <div className={styles.inputSection}>
                                        <form className={styles.inputForm} onSubmit={handleSendHelpdeskMessage}>
                                            <input
                                                className={styles.messageInput}
                                                type="text"
                                                placeholder="Type your reply..."
                                                value={messageInput}
                                                onChange={(e) => setMessageInput(e.target.value)}
                                                disabled={ticketActionLoading}
                                            />
                                            <button
                                                className={styles.sendButton}
                                                type="submit"
                                                disabled={ticketActionLoading || !messageInput.trim()}
                                            >
                                                {ticketActionLoading ? 'Sending...' : 'Send'}
                                            </button>
                                        </form>

                                        {messageAttachments.length > 0 && (
                                            <div className={styles.attachmentPreviewList}>
                                                {messageAttachments.map((file) => (
                                                    <span key={file.name + file.size} className={styles.attachmentPill}>
                                                        {file.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.resolvedBanner}>
                                        This ticket is {titleCaseStatus(selectedStatus)}. Reopen it with context to continue chatting.
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                </div>

                {showReopenModal && (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modalCard}>
                            <h3>Reopen Ticket</h3>
                            <p>Please share context for reopening this ticket.</p>
                            <form onSubmit={handleReopenTicket}>
                                <textarea
                                    className={styles.formTextarea}
                                    rows={4}
                                    value={reopenContext}
                                    onChange={(e) => setReopenContext(e.target.value)}
                                    required
                                />
                                <div className={styles.modalActions}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => {
                                            setShowReopenModal(false);
                                            setReopenContext('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={ticketActionLoading || !reopenContext.trim()}
                                    >
                                        {ticketActionLoading ? 'Submitting...' : 'Reopen Ticket'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderLegacyMode = () => {
        if (loading && messages.length === 0) {
            return (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingText}>Loading conversation...</div>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.chatContainer}>
                {error && <div className={styles.errorDisplay}>{error}</div>}

                {conversation && (
                    <div className={styles.chatHeader}>
                        <div className={styles.chatTitle}>
                            {user_type === 'employee' ? `Chat with ${conversation.brand_name || `Brand ${brandId}`}` : 'Internal Support Chat'}
                        </div>
                        {unreadCount > 0 && (
                            <div className={styles.unreadIndicator}>
                                {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.messagesArea}>
                    {messages.length === 0 ? (
                        <div className={styles.emptyState}>
                            {user_type === 'employee'
                                ? 'No messages yet. Start the conversation!'
                                : 'Welcome to Internal Support! Send a message to get help from our team.'}
                        </div>
                    ) : (
                        messages.map((msg, i) => {
                            const isUser = isCurrentUserMessage(msg);
                            const displayName = getSenderDisplayName(msg);

                            return (
                                <div
                                    key={msg.id || i}
                                    className={`${styles.messageContainer} ${isUser ? styles.sender : styles.receiver} ${msg.isOptimistic ? styles.optimisticMessage : ''}`}
                                >
                                    <div className={`${styles.messageBubble} ${isUser ? styles.sender : styles.receiver}`}>
                                        <div className={`${styles.senderName} ${isUser ? styles.sender : styles.receiver}`}>{displayName}</div>
                                        <div className={styles.messageContent}>{msg.content}</div>
                                        <div className={`${styles.messageFooter} ${isUser ? styles.sender : styles.receiver}`}>
                                            <span>{formatTime(msg.sent_at || msg.time)}</span>
                                            {msg.isOptimistic && <span className={styles.sendingStatus}>Sending...</span>}
                                            {isUser && !msg.isOptimistic && (
                                                <span className={styles.readStatus}>
                                                    {(user_type === 'appuser' && msg.is_read_by_employee) ||
                                                        (user_type === 'employee' && msg.is_read_by_brand)
                                                        ? 'Read'
                                                        : 'Sent'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={chatEndRef} />
                </div>

                <div className={styles.inputSection}>
                    <form className={styles.inputForm} onSubmit={handleLegacySend}>
                        <input
                            type="text"
                            placeholder={sendingMessage ? 'Sending...' : 'Type your message...'}
                            value={legacyInput}
                            onChange={(e) => setLegacyInput(e.target.value)}
                            disabled={sendingMessage}
                            autoFocus
                            className={styles.messageInput}
                        />
                        <button type="submit" disabled={!legacyInput.trim() || sendingMessage} className={styles.sendButton}>
                            {sendingMessage ? 'Sending...' : 'Send'}
                        </button>
                    </form>
                </div>
            </div>
        );
    };

    return isHelpdeskMode ? renderHelpdeskMode() : renderLegacyMode();
}